// src/hooks/useMarketSummary.js
import { useEffect, useState, useCallback } from "react";
import { supabase } from "services/supabaseClient";

/* FIXED CURVE MAP — using your provided IDs, 1M/3M dropped */
const FIXED_CURVE_MAP = [
  { tenor: "1y", ust: "ust-1y", sofr: "sofr-swap-1y", muniAAA: "muni-aaa-go-1y", muniA: "muni-a-go-1y" },
  { tenor: "2y", ust: "ust-2y", sofr: "sofr-swap-2y", muniAAA: "muni-aaa-go-2y", muniA: "muni-a-go-2y" },
  { tenor: "3y", ust: "ust-3y", sofr: "sofr-swap-3y", muniAAA: "muni-aaa-go-3y", muniA: "muni-a-go-3y" },
  { tenor: "5y", ust: "ust-5y", sofr: "sofr-swap-5y", muniAAA: "muni-aaa-go-5y", muniA: "muni-a-go-5y" },
  { tenor: "7y", ust: "ust-7y", sofr: "sofr-swap-7y", muniAAA: "muni-aaa-go-7y", muniA: "muni-a-go-7y" },
  { tenor: "10y", ust: "ust-10y", sofr: "sofr-swap-10y", muniAAA: "muni-aaa-go-10y", muniA: "muni-a-go-10y" },
  { tenor: "20y", ust: "ust-20y", sofr: "sofr-swap-20y", muniAAA: "muni-aaa-go-20y", muniA: "muni-a-go-20y" },
  { tenor: "30y", ust: "ust-30y", sofr: "sofr-swap-30y", muniAAA: "muni-aaa-go-30y", muniA: "muni-a-go-30y" },
];

const MARKETMAP_SYMBOL_PREFIXES = [
  "ust-",
  "sofr-",
  "muni-aaa-go-",
  "muni-a-go-",
  "oil-wti-",
  "ng-nymex-",
  "ng-fgtz3-",
  "ho-nymex-",
  "rbob-nymex-",
];

const MARKETMAP_EXACT_SYMBOLS = new Set([
  "fed-funds-eff",
  "mmf-govt-jpm",
  "muni-sifma",
]);

function isMarketMapSymbol(series) {
  const s = String(series || "").trim().toLowerCase();
  if (!s) return false;

  return (
    MARKETMAP_EXACT_SYMBOLS.has(s) ||
    MARKETMAP_SYMBOL_PREFIXES.some((prefix) => s.startsWith(prefix))
  );
}

function findKey(obj, candidates = []) {
  if (!obj) return null;
  for (const k of candidates) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) return k;
  }
  const lowerKeys = Object.keys(obj || {}).reduce((acc, key) => {
    acc[key.toLowerCase()] = key;
    return acc;
  }, {});
  for (const c of candidates) {
    const lc = String(c).toLowerCase();
    if (lowerKeys[lc]) return lowerKeys[lc];
  }
  return null;
}

function parseDateMaybe(v) {
  if (!v) return null;
  if (v instanceof Date && !isNaN(v)) return v;
  const d = new Date(v);
  if (!isNaN(d.getTime())) return d;
  const n = Number(v);
  if (!Number.isNaN(n)) {
    const maybe = new Date(n);
    if (!isNaN(maybe.getTime())) return maybe;
  }
  return null;
}

function dateSortValue(date) {
  if (!date) return 0;

  if (typeof date === "string") {
    return new Date(`${date}T00:00:00`).getTime();
  }

  if (date instanceof Date && !isNaN(date)) {
    return date.getTime();
  }

  return 0;
}

export default function useMarketSummary() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [latestMap, setLatestMap] = useState({});
  const [yieldCurve, setYieldCurve] = useState(null);
  const [lastAsOf, setLastAsOf] = useState(null);
  const [yieldCurveStatus, setYieldCurveStatus] = useState("unknown");

  const [distinctSymbols, setDistinctSymbols] = useState([]);
  const [distinctTenors, setDistinctTenors] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [rateLikeSymbols, setRateLikeSymbols] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: sbErr } = await supabase
        .from("market_data")
        .select("*")
        .order("date", { ascending: false, nulls: "last" })
        .limit(10000);

      if (sbErr) throw sbErr;

      if (!data || data.length === 0) {
        setLatestMap({});
        setYieldCurve(null);
        setYieldCurveStatus("no-rows");
        setDistinctSymbols([]);
        setDistinctTenors([]);
        setRowCount(0);
        setLastAsOf(null);
        setRateLikeSymbols([]);
        setLoading(false);
        return;
      }

      setRowCount(data.length);

      const normalized = data
        .map((r) => {
          const dateKey = findKey(r, ["date", "data_date", "as_of", "load_timestamp", "timestamp", "created_at"]);
          const seriesKey = findKey(r, ["security_id", "ticker", "symbol", "series", "name"]);
          const valueKey = findKey(r, ["value", "price", "mid", "close", "val"]);
          const tenorKey = findKey(r, ["tenor", "maturity", "term"]);
          const assetClassKey = findKey(r, ["asset_class", "assetclass", "asset"]);

          let parsedDate = null;
          if (dateKey && r[dateKey]) {
            const raw = String(r[dateKey]);
            if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
              parsedDate = raw;
            } else {
              parsedDate = parseDateMaybe(raw);
            }
          }

          const rawVal = valueKey ? r[valueKey] : null;
          const parsedValue =
            rawVal !== null && rawVal !== undefined ? Number(String(rawVal).replace(/,/g, "")) : null;

          const rawSeries = seriesKey ? String(r[seriesKey]) : r.name ? String(r.name) : "UNKNOWN";
          const series = rawSeries ? rawSeries.trim() : "UNKNOWN";

          const tenorRaw = tenorKey ? String(r[tenorKey]).trim() : null;
          const tenor = tenorRaw ? tenorRaw.trim() : null;
          const asset_class = assetClassKey ? String(r[assetClassKey]).toLowerCase() : null;

          return {
            raw: r,
            date: parsedDate,
            value: Number.isFinite(parsedValue) ? parsedValue : null,
            series,
            tenor,
            asset_class,
          };
        })
        .filter((x) => x.value !== null);

      const lmap = {};
      normalized.forEach((row) => {
        const sym = row.series || "UNKNOWN";
        const prev = lmap[sym];
        const t = dateSortValue(row.date);

        if (!prev || (t && t > (prev.date || 0))) {
          lmap[sym] = {
            val: row.value,
            date: t,
            label: sym,
            name: row.raw.name || sym,
            raw: row.raw,
            tenor: row.tenor,
            asset_class: row.asset_class,
            security_id: sym,
          };
        }
      });

      const symbols = Array.from(new Set(normalized.map((r) => r.series).filter(Boolean))).slice(0, 200);
      const tenors = Array.from(new Set(normalized.map((r) => r.tenor).filter(Boolean))).slice(0, 200);
      setDistinctSymbols(symbols);
      setDistinctTenors(tenors);

      const marketMapRows = normalized.filter((r) => isMarketMapSymbol(r.series));

      const lastDateVal = marketMapRows.reduce((acc, r) => {
        if (!r.date) return acc;

        const accT = dateSortValue(acc);
        const rowT = dateSortValue(r.date);

        return !acc || (rowT && rowT > accT) ? r.date : acc;
      }, null);

      setLastAsOf(lastDateVal);

      const rateLike = symbols
        .filter((s) => /UST|TREAS|SOFR|SWAP|USGG|GOVT|MUNI/i.test(s))
        .slice(0, 50);
      setRateLikeSymbols(rateLike);

      const lookupInLmap = (key) => {
        if (!key) return null;
        if (lmap[key]) return lmap[key];
        if (lmap[key.toUpperCase()]) return lmap[key.toUpperCase()];
        if (lmap[key.toLowerCase()]) return lmap[key.toLowerCase()];
        const lk = Object.keys(lmap || {}).find(
          (k) => k && k.toLowerCase().includes(String(key).toLowerCase())
        );
        if (lk) return lmap[lk];
        return null;
      };

      const ycRaw = [];
      for (const m of FIXED_CURVE_MAP) {
        const ustEntry = lookupInLmap(m.ust);
        const sofrEntry = lookupInLmap(m.sofr);
        const muniAAAEntry = lookupInLmap(m.muniAAA);
        const muniAEntry = lookupInLmap(m.muniA);

        ycRaw.push({
          tenor: m.tenor,
          UST: ustEntry ? ustEntry.val : null,
          SOFR: sofrEntry ? sofrEntry.val : null,
          "Muni-AAA": muniAAAEntry ? muniAAAEntry.val : null,
          "Muni-A": muniAEntry ? muniAEntry.val : null,
        });
      }

      const ycFiltered = ycRaw.filter(
        (r) => r.UST !== null || r.SOFR !== null || r["Muni-AAA"] !== null || r["Muni-A"] !== null
      );

      setLatestMap(lmap);
      setYieldCurve(ycFiltered.length ? ycFiltered : null);
      setYieldCurveStatus(ycFiltered.length ? "ok" : "missing-series");
    } catch (err) {
      console.error("[useMarketSummary] error:", err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    loading,
    error,
    latestMap,
    yieldCurve,
    lastAsOf,
    yieldCurveStatus,
    distinctSymbols,
    distinctTenors,
    rowCount,
    rateLikeSymbols,
    refresh: load,
  };
}